/* ==========================================================================
   JT Print Service front-end: Holiday Workshop edition
   Color modes, scroll reveals, bouncy letters, Christmas countdown, snowfall,
   shakeable snow globe, gift shelf filters, ornament name preview, and
   confetti when something lands in the cart. Plain ES module, no libraries.
   ========================================================================== */

const root = document.documentElement;
const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

const store = {
  get(key) {
    try {
      return localStorage.getItem(key);
    } catch (e) {
      return null;
    }
  },
  set(key, value) {
    try {
      localStorage.setItem(key, value);
    } catch (e) {
      /* storage blocked: the page still works */
    }
  },
};

/* --------------------------------------------------------------------------
   Color modes (holiday by default, category modes on pillar pages)
   -------------------------------------------------------------------------- */

const MODES = ['holiday', '3d', 'laser', 'paper', 'apparel'];
const MODE_KEY = 'jt-mode-v2';
let modeTimer = null;

function applyMode(mode, animate = true) {
  if (!MODES.includes(mode)) mode = 'holiday';
  if (root.dataset.jtMode === mode) return;
  if (animate && !reduceMotion.matches) {
    root.classList.add('jt-mode-transition');
    clearTimeout(modeTimer);
    modeTimer = setTimeout(() => root.classList.remove('jt-mode-transition'), 700);
  }
  root.dataset.jtMode = mode;
}

const Mode = {
  current: () => root.dataset.jtMode || 'holiday',
  set(mode, { persist = true, animate = true } = {}) {
    applyMode(mode, animate);
    if (persist) store.set(MODE_KEY, Mode.current());
  },
};

function initModes(scope = document) {
  const pinned = scope.querySelector('[data-jt-page-mode]');
  if (pinned) applyMode(pinned.dataset.jtPageMode, false);

  scope.querySelectorAll('[data-jt-set-mode]').forEach((el) => {
    if (el.dataset.jtModeBound) return;
    el.dataset.jtModeBound = '1';
    el.addEventListener('click', () => store.set(MODE_KEY, el.dataset.jtSetMode));
  });
}

/* --------------------------------------------------------------------------
   Scroll reveals
   -------------------------------------------------------------------------- */

let revealObserver = null;

function initReveals(scope = document) {
  const targets = scope.querySelectorAll('[data-jt-reveal], [data-jt-stagger]');
  if (reduceMotion.matches || !('IntersectionObserver' in window) || !root.classList.contains('jt-js')) {
    targets.forEach((t) => t.classList.add('is-in'));
    return;
  }
  if (!revealObserver) {
    revealObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-in');
          revealObserver.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -6% 0px', threshold: 0.08 }
    );
  }
  targets.forEach((t) => {
    if (!t.classList.contains('is-in')) revealObserver.observe(t);
  });
}

/* --------------------------------------------------------------------------
   Bouncy letters: wraps every letter so hovering makes it hop
   -------------------------------------------------------------------------- */

function initLetters(scope = document) {
  scope.querySelectorAll('.jt-letters').forEach((el) => {
    if (el.dataset.jtLettersBound) return;
    el.dataset.jtLettersBound = '1';
    if (!el.getAttribute('aria-label')) el.setAttribute('aria-label', el.textContent.trim().replace(/\s+/g, ' '));

    const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
    const nodes = [];
    while (walker.nextNode()) nodes.push(walker.currentNode);

    nodes.forEach((node) => {
      const parts = node.textContent.split(/(\s+)/);
      const frag = document.createDocumentFragment();
      parts.forEach((part) => {
        if (!part) return;
        if (/^\s+$/.test(part)) {
          frag.appendChild(document.createTextNode(' '));
          return;
        }
        const word = document.createElement('span');
        word.className = 'jt-word';
        word.setAttribute('aria-hidden', 'true');
        [...part].forEach((ch) => {
          const letter = document.createElement('span');
          letter.className = 'jt-letter';
          letter.textContent = ch;
          word.appendChild(letter);
        });
        frag.appendChild(word);
      });
      node.replaceWith(frag);
    });
  });
}

/* --------------------------------------------------------------------------
   Christmas countdown
   -------------------------------------------------------------------------- */

function daysUntilChristmas(now = new Date()) {
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const xmas = new Date(now.getFullYear(), 11, 25);
  return Math.round((xmas - today) / 86400000);
}

function initCountdown(scope = document) {
  const days = daysUntilChristmas();
  scope.querySelectorAll('[data-jt-countdown]').forEach((el) => {
    if (days > 0) el.textContent = String(days);
    else el.textContent = '0';
  });
  scope.querySelectorAll('[data-jt-xmas-label]').forEach((el) => {
    let text;
    if (days > 1) text = `${days} sleeps till Christmas`;
    else if (days === 1) text = '1 more sleep till Christmas!';
    else if (days === 0) text = 'Merry Christmas!';
    else text = 'Merry Christmas & happy New Year!';
    el.textContent = text;
  });
}

/* --------------------------------------------------------------------------
   Snowfall across the whole site
   -------------------------------------------------------------------------- */

const SNOW_KEY = 'jt-snow';
const Snow = {
  canvas: null,
  ctx: null,
  flakes: [],
  raf: 0,
  running: false,
  width: 0,
  height: 0,
  dpr: 1,

  enabled() {
    if (!root.classList.contains('jt-snow-enabled')) return false;
    const pref = store.get(SNOW_KEY);
    if (pref === 'off') return false;
    if (pref === 'on') return true;
    return !reduceMotion.matches;
  },

  setup() {
    if (this.canvas) return;
    this.canvas = document.createElement('canvas');
    this.canvas.className = 'jt-snow';
    this.canvas.setAttribute('aria-hidden', 'true');
    document.body.appendChild(this.canvas);
    this.ctx = this.canvas.getContext('2d');
    this.resize();
    window.addEventListener('resize', () => this.resize(), { passive: true });
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) this.stop();
      else if (this.enabled()) this.start();
    });
  },

  resize() {
    if (!this.canvas) return;
    this.dpr = Math.min(window.devicePixelRatio || 1, 1.5);
    this.width = window.innerWidth;
    this.height = window.innerHeight;
    this.canvas.width = Math.round(this.width * this.dpr);
    this.canvas.height = Math.round(this.height * this.dpr);
    this.ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    const target = Math.round(Math.min(70, Math.max(18, this.width / 20)));
    while (this.flakes.length < target) this.flakes.push(this.makeFlake(true));
    this.flakes.length = target;
  },

  makeFlake(anywhere) {
    const r = 1.2 + Math.random() * Math.random() * 3.4;
    return {
      x: Math.random() * this.width,
      y: anywhere ? Math.random() * this.height : -10 - Math.random() * 40,
      r,
      vy: 0.25 + r * 0.22 + Math.random() * 0.3,
      phase: Math.random() * Math.PI * 2,
      sway: 0.3 + Math.random() * 0.7,
    };
  },

  tick(t) {
    const { ctx } = this;
    ctx.clearRect(0, 0, this.width, this.height);
    ctx.fillStyle = 'rgba(255, 255, 255, 0.95)';
    ctx.strokeStyle = 'rgba(110, 160, 205, 0.55)';
    ctx.lineWidth = 1;
    for (const f of this.flakes) {
      f.y += f.vy;
      f.x += Math.sin(t / 1400 + f.phase) * f.sway * 0.4;
      if (f.y - f.r > this.height) Object.assign(f, this.makeFlake(false));
      if (f.x < -10) f.x = this.width + 10;
      if (f.x > this.width + 10) f.x = -10;
      ctx.beginPath();
      ctx.arc(f.x, f.y, f.r, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
    }
    this.raf = requestAnimationFrame((n) => this.tick(n));
  },

  start() {
    this.setup();
    if (this.running) return;
    this.running = true;
    root.classList.remove('jt-snow-off');
    this.raf = requestAnimationFrame((n) => this.tick(n));
  },

  stop() {
    this.running = false;
    cancelAnimationFrame(this.raf);
    if (this.ctx) this.ctx.clearRect(0, 0, this.width, this.height);
  },

  toggle() {
    const next = !(this.running && !root.classList.contains('jt-snow-off'));
    store.set(SNOW_KEY, next ? 'on' : 'off');
    if (next) this.start();
    else {
      this.stop();
      root.classList.add('jt-snow-off');
    }
    syncSnowButtons();
  },
};

function syncSnowButtons() {
  const on = Snow.running && !root.classList.contains('jt-snow-off');
  document.querySelectorAll('[data-jt-snow-toggle]').forEach((btn) => {
    btn.setAttribute('aria-pressed', on ? 'true' : 'false');
    const label = btn.querySelector('[data-jt-snow-label]');
    if (label) label.textContent = on ? 'Snow: on' : 'Snow: off';
  });
}

function initSnow(scope = document) {
  scope.querySelectorAll('[data-jt-snow-toggle]').forEach((btn) => {
    if (btn.dataset.jtSnowBound) return;
    btn.dataset.jtSnowBound = '1';
    if (!root.classList.contains('jt-snow-enabled')) {
      btn.hidden = true;
      return;
    }
    btn.addEventListener('click', () => Snow.toggle());
  });
  if (Snow.enabled() && !Snow.running) Snow.start();
  else if (!Snow.enabled()) root.classList.add('jt-snow-off');
  syncSnowButtons();
}

/* --------------------------------------------------------------------------
   Snow globe: tap or click to shake, snow swirls and settles
   -------------------------------------------------------------------------- */

function initGlobes(scope = document) {
  scope.querySelectorAll('[data-jt-globe]').forEach((globe) => {
    if (globe.dataset.jtGlobeBound) return;
    globe.dataset.jtGlobeBound = '1';

    const button = globe.querySelector('[data-jt-globe-shake]');
    const canvas = globe.querySelector('[data-jt-globe-snow]');
    if (!button || !canvas) return;
    const ctx = canvas.getContext('2d');
    let size = 0;
    let dpr = 1;
    let raf = 0;
    let visible = true;
    let flakes = [];

    const fit = () => {
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      size = canvas.clientWidth;
      canvas.width = Math.round(size * dpr);
      canvas.height = Math.round(size * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      if (!flakes.length) {
        const count = Math.round(Math.max(40, Math.min(90, size / 4)));
        flakes = Array.from({ length: count }, () => ({
          x: size / 2 + (Math.random() - 0.5) * size * 0.6,
          y: size * (0.55 + Math.random() * 0.35),
          vx: 0,
          vy: 0,
          r: 1.1 + Math.random() * 2.2,
        }));
      }
    };

    const shake = (strength = 1) => {
      flakes.forEach((f) => {
        f.vx += (Math.random() - 0.5) * 7 * strength;
        f.vy -= (2 + Math.random() * 5) * strength;
      });
      if (!reduceMotion.matches) {
        globe.classList.remove('is-shaking');
        void globe.offsetWidth;
        globe.classList.add('is-shaking');
        setTimeout(() => globe.classList.remove('is-shaking'), 750);
      }
      globe.classList.add('was-shaken');
      start();
    };

    const step = () => {
      const R = size / 2;
      const cx = R;
      const cy = R;
      ctx.clearRect(0, 0, size, size);
      ctx.fillStyle = 'rgba(255, 255, 255, 0.95)';
      let moving = false;
      for (const f of flakes) {
        f.vy += 0.035;
        f.vx *= 0.975;
        f.vy *= 0.975;
        f.x += f.vx + Math.sin((f.y + f.r * 40) / 30) * 0.12;
        f.y += f.vy;
        const dx = f.x - cx;
        const dy = f.y - cy;
        const dist = Math.hypot(dx, dy);
        const limit = R * 0.86 - f.r;
        if (dist > limit) {
          const k = limit / dist;
          f.x = cx + dx * k;
          f.y = cy + dy * k;
          f.vx *= -0.3;
          f.vy *= -0.3;
        }
        if (Math.abs(f.vx) + Math.abs(f.vy) > 0.06) moving = true;
        ctx.beginPath();
        ctx.arc(f.x, f.y, f.r, 0, Math.PI * 2);
        ctx.fill();
      }
      if (moving && visible) raf = requestAnimationFrame(step);
      else raf = 0;
    };

    const start = () => {
      if (!raf) raf = requestAnimationFrame(step);
    };

    fit();
    step();
    window.addEventListener('resize', () => {
      fit();
      start();
    });

    button.addEventListener('click', () => shake(1));

    // Shake it with a quick drag as well
    let last = null;
    button.addEventListener('pointermove', (e) => {
      if (e.buttons !== 1) return;
      const now = { x: e.clientX, y: e.clientY, t: performance.now() };
      if (last) {
        const speed = Math.hypot(now.x - last.x, now.y - last.y) / Math.max(1, now.t - last.t);
        if (speed > 1.2) shake(0.35);
      }
      last = now;
    });
    button.addEventListener('pointerup', () => (last = null));

    if ('IntersectionObserver' in window) {
      new IntersectionObserver((entries) => {
        visible = entries[0].isIntersecting;
        if (visible) start();
      }).observe(globe);
    }

    // One gentle swirl on arrival so it looks alive
    if (!reduceMotion.matches) setTimeout(() => shake(0.7), 3500);
  });
}

/* --------------------------------------------------------------------------
   Gift shelf: filter chips with a little pop
   -------------------------------------------------------------------------- */

function matches(item, rule, value) {
  const price = Number(item.dataset.price || 0) / 100;
  const text = item.dataset.search || '';
  switch (rule) {
    case 'max':
      return price <= Number(value);
    case 'min':
      return price >= Number(value);
    case 'keyword':
      return String(value)
        .toLowerCase()
        .split(',')
        .map((w) => w.trim())
        .filter(Boolean)
        .some((w) => text.includes(w));
    default:
      return true;
  }
}

function initShelves(scope = document) {
  scope.querySelectorAll('[data-jt-shelf]').forEach((shelf) => {
    if (shelf.dataset.jtShelfBound) return;
    shelf.dataset.jtShelfBound = '1';

    const chips = [...shelf.querySelectorAll('[data-jt-chip]')];
    const items = [...shelf.querySelectorAll('[data-jt-gift]')];
    const empty = shelf.querySelector('[data-jt-shelf-empty]');
    const count = shelf.querySelector('[data-jt-shelf-count]');

    const apply = (chip) => {
      chips.forEach((c) => c.setAttribute('aria-pressed', c === chip ? 'true' : 'false'));
      const rule = chip ? chip.dataset.rule : 'all';
      const value = chip ? chip.dataset.value : '';
      let shown = 0;
      items.forEach((item) => {
        const ok = matches(item, rule, value);
        if (ok) {
          if (item.hidden) {
            item.hidden = false;
            item.style.setProperty('--pop-delay', `${Math.min(shown, 8) * 45}ms`);
            item.classList.remove('is-popping');
            void item.offsetWidth;
            item.classList.add('is-popping');
          }
          shown += 1;
        } else {
          item.hidden = true;
        }
      });
      if (empty) empty.hidden = shown > 0;
      if (count) count.textContent = `${shown} ${shown === 1 ? 'gift' : 'gifts'}`;
    };

    chips.forEach((chip) => {
      chip.addEventListener('click', () => {
        apply(chip);
        if (!reduceMotion.matches) {
          chip.classList.remove('is-boing');
          void chip.offsetWidth;
          chip.classList.add('is-boing');
        }
      });
    });

    apply(chips.find((c) => c.getAttribute('aria-pressed') === 'true') || chips[0] || null);
  });
}

/* --------------------------------------------------------------------------
   Ornament name preview
   -------------------------------------------------------------------------- */

function initOrnaments(scope = document) {
  scope.querySelectorAll('[data-jt-ornament]').forEach((wrap) => {
    if (wrap.dataset.jtOrnBound) return;
    wrap.dataset.jtOrnBound = '1';

    const input = wrap.querySelector('[data-jt-ornament-input]');
    const nameEls = wrap.querySelectorAll('[data-jt-ornament-name]');
    const art = wrap.querySelector('[data-jt-ornament-art]');
    const cta = wrap.querySelector('[data-jt-ornament-cta]');
    const swatches = [...wrap.querySelectorAll('[data-jt-ornament-color]')];
    const fallback = input ? input.getAttribute('placeholder') || 'Your name' : 'Your name';
    let colorName = swatches[0] ? swatches[0].dataset.label : 'Red';

    const update = () => {
      const raw = (input && input.value.trim()) || '';
      const name = (raw || fallback).slice(0, 14);
      nameEls.forEach((el) => {
        el.textContent = name;
        const len = name.length;
        el.style.setProperty('--name-size', len <= 5 ? 1 : len <= 8 ? 0.8 : len <= 11 ? 0.62 : 0.5);
      });
      if (cta) {
        const base = cta.dataset.base || cta.getAttribute('href');
        cta.dataset.base = base;
        const note = raw
          ? `Hi! I'd love a custom ${colorName.toLowerCase()} ornament that says "${raw}".`
          : `Hi! I'd love a custom ornament.`;
        const url = new URL(base, window.location.origin);
        url.searchParams.set('jt_note', note);
        cta.setAttribute('href', url.pathname + url.search);
      }
    };

    if (input) {
      input.addEventListener('input', () => {
        update();
        if (art && !reduceMotion.matches) {
          art.classList.remove('is-jiggle');
          void art.offsetWidth;
          art.classList.add('is-jiggle');
        }
      });
    }

    swatches.forEach((sw) => {
      sw.addEventListener('click', () => {
        swatches.forEach((s) => s.setAttribute('aria-pressed', s === sw ? 'true' : 'false'));
        colorName = sw.dataset.label || colorName;
        if (art) {
          art.style.setProperty('--orn', sw.dataset.jtOrnamentColor);
          art.style.setProperty('--orn-deep', sw.dataset.deep || sw.dataset.jtOrnamentColor);
        }
        update();
      });
    });

    update();
  });
}

/* Prefill the contact form when we arrive from the ornament builder */
function prefillContact() {
  const params = new URLSearchParams(window.location.search);
  const note = params.get('jt_note');
  if (!note) return;
  const field = document.querySelector('textarea[name="contact[body]"]');
  if (field && !field.value) field.value = note;
}

/* --------------------------------------------------------------------------
   Confetti when something is added to the cart
   -------------------------------------------------------------------------- */

const CONFETTI_COLORS = ['#e0252f', '#16a05d', '#ffc53d', '#6ec6ff', '#ffffff', '#ff8fb1'];

function confetti(x, y) {
  if (reduceMotion.matches || !root.classList.contains('jt-confetti-on')) return;
  const canvas = document.createElement('canvas');
  canvas.className = 'jt-confetti';
  canvas.setAttribute('aria-hidden', 'true');
  document.body.appendChild(canvas);
  const ctx = canvas.getContext('2d');
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  const w = window.innerWidth;
  const h = window.innerHeight;
  canvas.width = w * dpr;
  canvas.height = h * dpr;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

  const bits = Array.from({ length: 80 }, () => {
    const angle = -Math.PI / 2 + (Math.random() - 0.5) * Math.PI * 1.1;
    const speed = 5 + Math.random() * 8;
    return {
      x,
      y,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed,
      size: 5 + Math.random() * 6,
      rot: Math.random() * Math.PI,
      vr: (Math.random() - 0.5) * 0.4,
      color: CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)],
      round: Math.random() < 0.35,
    };
  });

  const born = performance.now();
  const frame = (now) => {
    const age = now - born;
    ctx.clearRect(0, 0, w, h);
    ctx.globalAlpha = Math.max(0, 1 - age / 1500);
    for (const b of bits) {
      b.vy += 0.28;
      b.vx *= 0.985;
      b.x += b.vx;
      b.y += b.vy;
      b.rot += b.vr;
      ctx.save();
      ctx.translate(b.x, b.y);
      ctx.rotate(b.rot);
      ctx.fillStyle = b.color;
      ctx.strokeStyle = '#26211c';
      ctx.lineWidth = 1;
      if (b.round) {
        ctx.beginPath();
        ctx.arc(0, 0, b.size / 2, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
      } else {
        ctx.fillRect(-b.size / 2, -b.size / 4, b.size, b.size / 2);
        ctx.strokeRect(-b.size / 2, -b.size / 4, b.size, b.size / 2);
      }
      ctx.restore();
    }
    if (age < 1500) requestAnimationFrame(frame);
    else canvas.remove();
  };
  requestAnimationFrame(frame);
}

function initConfetti() {
  if (initConfetti.bound) return;
  initConfetti.bound = true;
  document.addEventListener('cart:update', (event) => {
    const data = (event.detail && event.detail.data) || {};
    if (data.didError || data.source !== 'product-form-component') return;
    const source = event.target instanceof Element ? event.target : null;
    const button = source ? source.querySelector('button[type="submit"], .add-to-cart-button') || source : null;
    const rect = button ? button.getBoundingClientRect() : null;
    const x = rect && rect.width ? rect.left + rect.width / 2 : window.innerWidth / 2;
    const y = rect && rect.height ? rect.top + rect.height / 2 : window.innerHeight / 2;
    confetti(x, y);
  });
}

/* --------------------------------------------------------------------------
   Scroll story (legacy section, still available for other pages)
   -------------------------------------------------------------------------- */

function initStory(scope = document) {
  scope.querySelectorAll('[data-jt-story]').forEach((story) => {
    if (story.dataset.jtStoryBound) return;
    story.dataset.jtStoryBound = '1';
    const steps = [...story.querySelectorAll('[data-jt-step]')];
    if (!steps.length) return;
    let ticking = false;
    const update = () => {
      ticking = false;
      const focusLine = window.innerHeight * 0.5;
      let active = 0;
      let progress = 0;
      steps.forEach((step, i) => {
        const r = step.getBoundingClientRect();
        if (r.top <= focusLine) {
          active = i;
          progress = Math.min(1, Math.max(0, (focusLine - r.top) / Math.max(r.height, 1)));
        }
      });
      if (story.dataset.activeStep !== String(active)) {
        story.dataset.activeStep = String(active);
        steps.forEach((s, i) => s.classList.toggle('is-active', i === active));
      }
      story.style.setProperty('--jt-step-progress', progress.toFixed(4));
    };
    window.addEventListener('scroll', () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    }, { passive: true });
    update();
  });
}

/* --------------------------------------------------------------------------
   Boot
   -------------------------------------------------------------------------- */

function init(scope = document) {
  initModes(scope);
  initLetters(scope);
  initReveals(scope);
  initCountdown(scope);
  initSnow(scope);
  initGlobes(scope);
  initShelves(scope);
  initOrnaments(scope);
  initStory(scope);
}

function boot() {
  // The inline head script hides reveal targets; if this file loaded too late it gave up, so show everything
  if (!window.__jtFallback) root.classList.add('jt-js');
  if (!root.dataset.jtMode) applyMode(store.get(MODE_KEY) || 'holiday', false);
  init(document);
  initConfetti();
  prefillContact();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot);
} else {
  boot();
}

// Theme editor re-renders sections without a page load
document.addEventListener('shopify:section:load', (e) => init(e.target));

window.JT = Object.assign(window.JT || {}, { Mode, Snow, confetti, init });

export { Mode, Snow, confetti, init };
